import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { reloadOnceForStaleChunk } from './lazyWithReload';
import { CODES as SITE_LANGUAGES, APP_LANGUAGES, isAppLanguage } from './siteLanguages';
import es from '../locales/es.json';
import catalogueStrings from '../locales/catalogue';
import onboardingStrings from '../locales/onboarding';
import onlineStoreStrings from '../locales/onlineStore';
import bookmeStrings from '../locales/bookme';

// Spanish ships with the app (it is also the fallback). English and Catalan
// are large and most visitors never use them, so each loads as its own chunk
// the first time it is needed — see ensureLanguage() below. Tests load them
// up front so they behave exactly as before.
const LAZY_LOCALES = {
  en: () => import(/* webpackChunkName: "locale-en" */ '../locales/en.json'),
  ca: () => import(/* webpackChunkName: "locale-ca" */ '../locales/ca.json'),
};

// Bundles that other modules register for a lazy language BEFORE its main
// file has arrived are held here and replayed, in order and with their own
// flags, right after it — so the final translations are identical to the
// old "everything bundled" setup (main file first, then every bundle).
const pending = { en: [], ca: [] };
const loaded = { es: true, en: false, ca: false };
const inflight = {};
// i18next creates addResourceBundle inside init(); set after it, below.
let addBundle = null;

// Loads a language's translations (once). Resolves when they are in place.
export function ensureLanguage(lng) {
  const base = String(lng || 'es').slice(0, 2);
  if (!(base in LAZY_LOCALES) || loaded[base]) return Promise.resolve();
  if (!inflight[base]) {
    inflight[base] = LAZY_LOCALES[base]().then((mod) => {
      addBundle(base, 'translation', mod.default || mod, false, true);
      loaded[base] = true;
      pending[base].splice(0).forEach((args) => addBundle(...args));
    }).catch((err) => {
      delete inflight[base];
      // A deploy changed the chunk name under a still-open tab: reload once so
      // the fresh index.html loads the new locale chunk, instead of switching
      // the language with no strings (which shows Spanish fallback). The reload
      // navigates away, so hang here rather than resolving with nothing.
      if (reloadOnceForStaleChunk(err)) return new Promise(() => {});
      throw err;
    });
  }
  return inflight[base];
}

// Fetches the other languages in the background (documents and the help
// assistant can be produced in a language other than the screen's).
export function preloadAllLanguages() {
  return Promise.all(Object.keys(LAZY_LOCALES).map((l) => ensureLanguage(l).catch(() => {})));
}

// How the APP picks its language: the saved choice, then the browser.
export const APP_DETECTION = {
  order: ['localStorage', 'navigator'],
  lookupLocalStorage: 'zbilling_lang',
  caches: ['localStorage'],
  // The app screens are translated into es/en/ca only: a browser asking for
  // "fr-FR, fr, en" gets English, exactly as before the website learnt French
  // (a disguised code matches no language, so detection moves on to the next
  // one, or to Spanish).
  convertDetectedLanguage: (lng) => (isAppLanguage(lng) ? lng : `zz-${lng}`),
  // …and a website-only language is never saved as the app's language.
  excludeCacheFor: ['cimode', ...SITE_LANGUAGES.filter((l) => !APP_LANGUAGES.includes(l))],
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      es: { translation: es },
    },
    fallbackLng: 'es',
    // Every language of the public website (lib/siteLanguages.js), so its
    // pages can be drawn in any of them (lib/siteI18n.js). The APP itself
    // still only detects and saves its own three languages — see below.
    supportedLngs: SITE_LANGUAGES,
    detection: APP_DETECTION,
    interpolation: { escapeValue: false },
  });

// Hold bundles registered for a language whose main file has not arrived yet
// (replayed in ensureLanguage, after the main file).
addBundle = i18n.addResourceBundle.bind(i18n);
i18n.addResourceBundle = (lng, ns, resources, deep, overwrite) => {
  const base = String(lng || '').slice(0, 2);
  if (base in pending && !loaded[base]) {
    pending[base].push([lng, ns, resources, deep, overwrite]);
    return i18n;
  }
  return addBundle(lng, ns, resources, deep, overwrite);
};

// Every language switch waits for that language's file first, so nothing
// ever shows half-translated (Layout forcing English for PK accounts, the
// language menus, i18n.changeLanguage anywhere).
const changeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (lng, callback) => (
  ensureLanguage(lng).catch(() => {}).then(() => changeLanguage(lng, callback))
);

// Feature bundles kept outside the main locale files (merged deep, never
// overwriting an existing key).
[catalogueStrings, onboardingStrings, onlineStoreStrings, bookmeStrings].forEach((bundle) => {
  Object.entries(bundle).forEach(([lng, res]) => {
    i18n.addResourceBundle(lng, 'translation', res, true, false);
  });
});

// In tests every language is loaded synchronously, exactly as before.
if (process.env.NODE_ENV === 'test') {
  /* eslint-disable global-require */
  const files = { en: require('../locales/en.json'), ca: require('../locales/ca.json') };
  /* eslint-enable global-require */
  Object.entries(files).forEach(([lng, res]) => {
    addBundle(lng, 'translation', res, false, true);
    loaded[lng] = true;
    pending[lng].splice(0).forEach((args) => addBundle(...args));
  });
}

// Ready when the detected language's file is in place (index.js waits for
// this before drawing the app).
export const i18nReady = ensureLanguage(i18n.language).catch(() => {});

export default i18n;

// Keep <html lang> in step with the UI language. index.html ships lang="es";
// a wrong/stale value makes Chrome classify a Spanish page as English and offer
// (or, with "always translate", silently apply) a translation — which rewrites
// the DOM under React and crashes pages (see lib/domGuard.js).
// A public website page (components/public/SiteLang.jsx) reads in the language
// of its address, whatever the app's language: while one is shown, <html
// lang> is that language.
let siteHtmlLang = null;
const syncHtmlLang = (lng) => {
  try { document.documentElement.lang = (siteHtmlLang || lng || 'es').slice(0, 2); } catch { /* SSR / tests */ }
};
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export function setSiteHtmlLang(lng) {
  siteHtmlLang = lng || null;
  syncHtmlLang(i18n.language);
}
